import classnames from 'classnames';
import { deepEqual } from 'fast-equals';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { addVariants, useEventCallback, useOverriddenState } from '@agilemd/ui-utils';

import TabContext from './Context.ts';
import styles from './styles.module.scss';

export const VARIANTS = ['default'];

const Tabs = ({
  variant = 'default',
  className = '',
  children,
  initialTab = 0,
  currentTab = null,
  comparator: comparatorFromProps = deepEqual,
  onChange: onChangeFromProps = () => 0
}) => {
  const comparator = useEventCallback(comparatorFromProps);
  const [selectedTab, setTab] = useOverriddenState(initialTab, currentTab, {
    onChange: onChangeFromProps,
    comparator
  });
  const context = useMemo(
    () => ({
      selectedTab,
      comparator,
      isSelected: tab => comparator(selectedTab, tab),
      setTab
    }),
    [selectedTab, comparator, setTab]
  );

  return (
    <TabContext.Provider value={context}>
      <div className={classnames(addVariants('aglmd-tabs', styles, variant), className)}>
        {children}
      </div>
    </TabContext.Provider>
  );
};

Tabs.propTypes = {
  /**
   * The `Tabs.List` and `Tabs.Pane`s to display
   */
  children: PropTypes.node.isRequired,
  /**
   * The initial tab to display should be provided if using currentTab
   * otherwise the first rendered tab is selected
   */
  initialTab: PropTypes.any,
  /**
   * If provided will override the current tab
   */
  currentTab: PropTypes.any,
  /**
   * The comparator for testing whether a tab is the selected tab
   * @deprecated Just default to deep equal comparisons
   */
  comparator: PropTypes.func,
  /**
   * Variant to use
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Class to apply
   */
  className: PropTypes.string,
  onChange: PropTypes.func
};

export default Tabs;
